import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Panel, Form, FormControl, FormGroup, Col, ControlLabel, InputGroup } from 'react-bootstrap';
import { bind } from '../../utils/decorators';
import * as ApiTypes from '../../models/ApiTypes';
import ObjektMessmittelUebernehmenModal from './ObjektMessmittelUebernehmen';
import ObjektAnlegen, { ObjektAnlegenHeader } from './ObjektAnlegen';

/** Komponenten zum Erfassen eines neuen Objektes vom Typ Agbasmgessgerät (kurz AGT) */
@observer
export default class ObjektAnlegenAGT extends ObjektAnlegen {

  @observable objekt: Partial<ApiTypes.IObjekt> = {
    objektid: '',
    hersteller: '',
    name: '',
    sernr: '',
    typbezeichnung: '',
    typ: 'AGT',
    bemerkung: ''
  };

  @observable objektMeta = {
    messkammerlaenge: '',
    fehlergrenze: '',
    genauigkeitsklasse: '',
    baumuster: '',
    baujahr: '',
    salzrußäquivalent: ''
  };

  async componentDidMount() {
    const objektVorlagen = await this.apiStore.objektVorlagen('agt');
    if (objektVorlagen.ok) {
      this.setObjektVorlagen(objektVorlagen.json);
    }
  }

  @bind async handleObjektAnlegenSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();

    const objekt = Object.assign({}, this.objekt);
    objekt.meta = [];
    objekt.meta.push({ bezeichnung: 'agt_messkammerlänge', inhalt: this.objektMeta.messkammerlaenge });
    objekt.meta.push({ bezeichnung: 'agt_fehlergrenze', inhalt: this.objektMeta.fehlergrenze });
    objekt.meta.push({ bezeichnung: 'agt_genauigkeitsklasse', inhalt: this.objektMeta.genauigkeitsklasse });
    objekt.meta.push({ bezeichnung: 'agt_baujahr', inhalt: this.objektMeta.baujahr });
    objekt.meta.push({ bezeichnung: 'agt_baumuster', inhalt: this.objektMeta.baumuster });
    objekt.meta.push({ bezeichnung: 'agt_salz_ruß_äquivalent', inhalt: this.objektMeta.salzrußäquivalent });

    if (this.props.onAddObjekt) {
      this.props.onAddObjekt(objekt);
    }
  }

  /** Übernimmt die Daten aus der gewählten Vorlage in das anzulegende Objekt */
  @bind @action uebernehmeObjektEigenschaften() {
    const vorlage = this.vorlageZuModell(this.objekt.hersteller, this.objekt.typbezeichnung);
    if (vorlage) {
      this.objekt.art = vorlage.art;
      // Klartextname setzen
      switch (this.objekt.art) {
        case ApiTypes.AGT_4GAS:
          this.objekt.name = '4-Gas Tester';
          break;
        case ApiTypes.AGT_OPAZIMETER:
          this.objekt.name = 'Opazimeter';
          break;
        case ApiTypes.AGT_KOMBI:
          this.objekt.name = 'Kombigerät';
          break;
        case ApiTypes.AGT_PARTIKEL_DC:
          this.objekt.name = 'Partikelzähler (Diffusion Charging)';
          break;
        case ApiTypes.AGT_PARTIKEL_CPC:
          this.objekt.name = 'Partikelzähler (Condensation Particle Counter)';
          break;
        default:
          this.objekt.name = '';
      }
      // Einige der Objektdaten können in der Vorlage (für ein Objekt) vordefiniert werden
      // Diese werden direkt beim Anlagen übernommen
      if (this.objekt.art === ApiTypes.AGT_4GAS || this.objekt.art === ApiTypes.AGT_KOMBI) {
        let meta = this.vorlageMetaDatum(vorlage, 'agt_genauigkeitsklasse');
        this.objektMeta.genauigkeitsklasse = meta ? meta : '';
      }
      if (this.objekt.art === ApiTypes.AGT_OPAZIMETER || this.objekt.art === ApiTypes.AGT_KOMBI) {
        let meta = this.vorlageMetaDatum(vorlage, 'agt_fehlergrenze');
        this.objektMeta.fehlergrenze = meta ? meta : '';
        meta = this.vorlageMetaDatum(vorlage, 'agt_messkammerlänge');
        this.objektMeta.messkammerlaenge = meta ? meta : '';
      }
      if (this.objekt.art === ApiTypes.AGT_PARTIKEL_DC || this.objekt.art === ApiTypes.AGT_PARTIKEL_CPC) {
        let meta = this.vorlageMetaDatum(vorlage, 'agt_salz_ruß_äquivalent');
        this.objektMeta.salzrußäquivalent = meta ? meta : '';
      }
    } else {
      this.objekt.art = '';
    }
  }

  /** Übernimmt die Daten aus einem Messmittel der ÜO, soweit diese im ÜO Bereich vorhanden sind */
  @bind @action uebernehmeMessmittelDaten(messmittel: ApiTypes.IMessmittelUO) {
    this.objekt.objektid = messmittel.pmnummer;
    this.objekt.messmittelkennung = messmittel.pmnummer;
    const vorlageHersteller = this.objektVorlagen.find(el => el.hersteller.toLocaleLowerCase() === messmittel.hersteller.toLocaleLowerCase());
    if (vorlageHersteller) {
      this.objekt.hersteller = vorlageHersteller.hersteller;
    }
    this.objekt.sernr = messmittel.seriennummer;
    // Bei der Übernahme aus einem ÜO Messmittel passt der Modellbezeichner wegen der freien Eingabe u.U. nicht zur Vorlage
    this.objektVorlagenTypen = this.vorlagenZuHersteller(messmittel.hersteller);
    // Zum Messmittel passende Vorlage wählen, wegen freier Eingabemöglichkeit im ÜO Umfeld nicht auf die Schreibweise achten
    const vorlage = this.vorlageZuModell(messmittel.hersteller, messmittel.name);
    if (vorlage) {
      this.objekt.typbezeichnung = vorlage.modell;
      this.uebernehmeObjektEigenschaften();
    }
    this.objektMeta.baumuster = messmittel.baumusterpruefung ? messmittel.baumusterpruefung : '';
    // @todo: Nicht vorhandene Vorlage visualisieren
    this.hideModal();
  }

  @bind @action handleEditValueChanged(event: React.FormEvent<FormControl>) {
    const value = (event.target as HTMLInputElement).value;
    const id = (event.target as HTMLInputElement).id;

    if (id in this.objekt) {
      this.objekt[id] = value;

      // Beim Wechsel des Herstellers müssen bereits gewählte abhängige Daten gelöscht werden
      if (id === 'hersteller') {
        this.objekt.typbezeichnung = '';
        this.objekt.art = '';
        // Auswählbare Vorlagen auf die für den Hersteller verfügbaren einschränken
        this.objektVorlagenTypen = this.vorlagenZuHersteller(this.objekt.hersteller);
      }
      // Beim Wechsel des Typs (aka Modell) die in der Objektvorlage hinterlegten Eigenschaften in as anzulegende Objekt übernehmen
      if (id === 'typbezeichnung') {
        this.uebernehmeObjektEigenschaften();
      }
      return;
    }

    if (id in this.objektMeta) {
      this.objektMeta[id] = value;
    }
  }

  validate() {
    let validated = (this.objekt.name && this.objekt.name.trim() !== '') && (this.objektMeta.baujahr.trim() !== '');
    // Validierung der Attribute hängt von der Art des Abgasmessgerätes ab, je nach Art müssen verschiedene Werte manuell hinterlegt werden
    if (((this.objekt.art === ApiTypes.AGT_4GAS) || (this.objekt.art === ApiTypes.AGT_KOMBI)) && (this.objektMeta.genauigkeitsklasse.trim() === '')) {
      validated = false;
    }
    if (((this.objekt.art === ApiTypes.AGT_OPAZIMETER) || (this.objekt.art === ApiTypes.AGT_KOMBI)) && ((this.objektMeta.fehlergrenze.trim() === '') || (this.objektMeta.messkammerlaenge.trim() === ''))) {
      validated = false;
    }
    console.debug(validated);
    return validated;
  }

  render() {
    return (
      <Form horizontal={true} onSubmit={this.handleObjektAnlegenSubmit}>
        <Panel className="panel-header-buttoned"
          header={<ObjektAnlegenHeader caption="Neues Abgasmessgerät anlegen" showModal={this.showModal}/>}
          footer={(
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
              <Button bsStyle="primary" disabled={!this.validate()} type="submit">Anlegen</Button>
            </div>
          )}
        >
          <FormGroup validationState={this.objekt.hersteller === '' ? 'error' : undefined} controlId="hersteller">
            <Col componentClass={ControlLabel} sm={3}>Hersteller</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objekt.hersteller} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  {this.objektVorlagenHersteller.map((name) => <option key={name} value={name}>{name}</option>)}
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.typbezeichnung === '' ? 'error' : undefined} controlId="typbezeichnung">
            <Col componentClass={ControlLabel} sm={3}>Typ</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.objekt.typbezeichnung} onChange={this.handleEditValueChanged} readOnly={!this.objekt.hersteller} disabled={!this.objekt.hersteller}>
                <option value="">{this.objekt.hersteller ? 'Bitte auswählen...' : 'Bitte Hersteller auswählen...'}</option>
                {
                  this.objektVorlagenTypen &&
                    this.objektVorlagenTypen.filter(vorlage => vorlage.hersteller === this.objekt.hersteller).map(
                      vorlage => <option key={vorlage.modell} value={vorlage.modell}>{vorlage.modell}</option>)}
                    )
                }
              </FormControl>
            </Col>
          </FormGroup>
          <FormGroup validationState={this.objekt.art === '' ? 'error' : undefined} controlId="name">
            <Col componentClass={ControlLabel} sm={3}>Art</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" type="text" label="Name" id="name" value={this.objekt.art} onChange={this.handleEditValueChanged} readOnly={true} disabled={true}>
                {/* @todo: Prüfen ob direkt aus Datenbank übernehmbar (Tabelle "AW_Objekt_Art") */}
                <option value="">Bitte Hersteller und Typ wählen...</option>
                <option value="4gas">4-Gas Tester</option>
                <option value="opazimeter">Opazimeter</option>
                <option value="kombi">Kombigerät</option>
                <option value="partikel_dc">Partikelzähler (Diffusion Charging)</option>
                <option value="partikel_cpc">Partikelzähler (Condensation Particle Counter)</option>
              </FormControl>
            </Col>
          </FormGroup>

          {/* Bei Opazimetern (bzw. Kombigeräten) ist die Eingabe der Messkammerlänge erforderlich */}
          {((this.objekt.art === ApiTypes.AGT_OPAZIMETER) || (this.objekt.art === ApiTypes.AGT_KOMBI)) && (
            <FormGroup validationState={this.objektMeta.messkammerlaenge === '' ? 'error' : undefined}  controlId="messkammerlaenge">
              <Col componentClass={ControlLabel} sm={3}>Messkammerlänge</Col>
              <Col sm={9}>
                <InputGroup>
                  <FormControl type="number" step="any" label="Messkammerlänge" id="messkammerlaenge" value={this.objektMeta.messkammerlaenge} onChange={this.handleEditValueChanged}/>
                  <InputGroup.Addon>mm</InputGroup.Addon>
                </InputGroup>
              </Col>
            </FormGroup>)
          }

          {/* Bei 4-Gas Testern (bzw. Kombigeräten) ist die Eingabe der Genauigkeitsklasse erforderlich */}
          {((this.objekt.art === ApiTypes.AGT_4GAS) || (this.objekt.art === ApiTypes.AGT_KOMBI)) && (
            <FormGroup validationState={this.objektMeta.genauigkeitsklasse === '' ? 'error' : undefined} controlId="genauigkeitsklasse">
            <Col componentClass={ControlLabel} sm={3}>Genauigkeitsklasse</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" type="text" label="Genauigkeitsklasse" id="genauigkeitsklasse" value={this.objektMeta.genauigkeitsklasse} onChange={this.handleEditValueChanged}>
                <option value="">Bitte auswählen...</option>
                <option value="00">00</option>
                <option value="0">0</option>
                <option value="1">1</option>
              </FormControl>
            </Col>
            </FormGroup>)
          }

          {/* Bei Opazimetern (bzw. Kombigeräten) ist die Eingabe der Fehlergrenze erforderlich */}
          {((this.objekt.art === ApiTypes.AGT_OPAZIMETER) || (this.objekt.art === ApiTypes.AGT_KOMBI)) && (
            <FormGroup validationState={this.objektMeta.fehlergrenze === '' ? 'error' : undefined} controlId="fehlergrenze">
            <Col componentClass={ControlLabel} sm={3}>Fehlergrenze</Col>
            <Col sm={9}>
              <InputGroup>
                <InputGroup.Addon>±</InputGroup.Addon>
                <FormControl componentClass="select" placeholder="select" type="text" label="Fehlergrenze" id="fehlergrenze" value={this.objektMeta.fehlergrenze} onChange={this.handleEditValueChanged}>
                  <option value="">Bitte auswählen...</option>
                  <option value="0,1">0,1</option>
                  <option value="0,3">0,3</option>
                </FormControl>
                <InputGroup.Addon>m<sup>-1</sup></InputGroup.Addon>
              </InputGroup>
            </Col>
            </FormGroup>)
          }

          {/* Bei Partikelzählern ist die Eingabe eines Salz/Ruß-Äquivalents zur Umrechung in der Auswertung erforderlich */}
          {(this.objekt.art === ApiTypes.AGT_PARTIKEL_DC || this.objekt.art === ApiTypes.AGT_PARTIKEL_CPC) && (
            <FormGroup validationState={this.objektMeta.salzrußäquivalent === '' ? 'error' : undefined} controlId="fehlergrenze">
            <Col componentClass={ControlLabel} sm={3}>Salz/Ruß Äquivalent</Col>
            <Col sm={9}>
              <InputGroup>
                <FormControl type="number" step="any" label="Salz/Ruß Äquivalent" id="salzrußäquivalent" value={this.objektMeta.salzrußäquivalent} onChange={this.handleEditValueChanged}/>
              </InputGroup>
            </Col>
            </FormGroup>)
          }

          <FormGroup validationState={this.objekt.sernr === '' ? 'error' : undefined} controlId="sernr">
            <Col componentClass={ControlLabel} sm={3}>Seriennr.</Col>
            <Col sm={9}>
              <FormControl type="text" label="Seriennr." id="sernr" value={this.objekt.sernr} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objekt.objektid === '' ? 'error' : undefined} controlId="objektid">
            <Col componentClass={ControlLabel} sm={3}>Kennzeichnung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Kennzeichnung" id="objektid" value={this.objekt.objektid} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objektMeta.baumuster === '' ? 'error' : undefined} controlId="baumuster">
            <Col componentClass={ControlLabel} sm={3}>Baumuster</Col>
            <Col sm={9}>
              <FormControl type="text" label="Baumuster" id="baumuster" value={this.objektMeta.baumuster} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup validationState={this.objektMeta.baujahr === '' ? 'error' : undefined} controlId="baujahr">
            <Col componentClass={ControlLabel} sm={3}>Baujahr</Col>
            <Col sm={9}>
              <FormControl type="text" label="Baujahr" id="baujahr" placeholder="JJJJ" value={this.objektMeta.baujahr} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="bemerkung">
            <Col componentClass={ControlLabel} sm={3}>Bemerkung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Bemerkung" id="bemerkung" value={this.objekt.bemerkung} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          {/* Die Funktion zur Übernahme der ÜO Messmitteldaten wird modal angezeigt */}
          <ObjektMessmittelUebernehmenModal kategorie="agt" onUebernehme={this.uebernehmeMessmittelDaten} show={this.modalVisible} onHideModal={this.hideModal}/>

        </Panel>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/objekte/ObjektAnlegenAGT.tsx